import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { listNewsletterDrafts, createNewsletterDraft, getNewsletterHistory } from "@/lib/newsletter2.functions";
import { DEFAULT_BLOCKS, newBlock } from "@/lib/newsletter2/blocks";
import { getResendStatus, syncResendChunk } from "@/lib/newsletter2-resend.functions";
import { toast } from "sonner";

export const Route = createFileRoute("/admin/bulten/")({
  head: () => ({ meta: [{ title: "Bülten merkezi – Admin" }, { name: "robots", content: "noindex, nofollow" }] }),
  component: NewsletterCenter,
});

const STATUS: Record<string, string> = { draft: "Taslak", scheduled: "Zamanlandı", sending: "Gönderiliyor", sent: "Gönderildi", failed: "Hatalı" };
const fmt = (s: string | null) => (s ? new Date(s).toLocaleString("tr-TR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—");

function NewsletterCenter() {
  const list = useServerFn(listNewsletterDrafts);
  const create = useServerFn(createNewsletterDraft);
  const history = useServerFn(getNewsletterHistory);
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const q = useQuery({ queryKey: ["nl2-drafts"], queryFn: () => list() });
  const h = useQuery({ queryKey: ["nl2-history"], queryFn: () => history() });
  const a = q.data?.audience;

  async function onNew() {
    setBusy(true);
    try {
      const r = await create({ data: { blocks: DEFAULT_BLOCKS.map(newBlock) } });
      nav({ to: "/admin/bulten/$id", params: { id: r.id } });
    } finally { setBusy(false); }
  }

  return (
    <div className="max-w-4xl space-y-10 pb-24">
      <div className="grid grid-cols-[minmax(0,1fr)_auto] items-end gap-4">
        <div className="min-w-0">
          <h1 className="font-serif text-3xl">Bülten</h1>
          <p className="text-sm text-ink/60 mt-1">Bloklarla hazırla, önizle, kendine test gönder.</p>
        </div>
        <button onClick={onNew} disabled={busy} className="shrink-0 px-4 py-2 bg-ink text-paper rounded-full text-sm disabled:opacity-50">+ Yeni bülten</button>
      </div>

      <section className="grid grid-cols-3 gap-3 text-center">
        <Stat label="Gönderilebilir abone" value={a?.eligible} />
        <Stat label="Kayıtlı abone" value={a?.subscribers} />
        <Stat label="Gönderim dışı" value={a?.suppressed} />
      </section>

      <ResendSync />

      <section>
        <h2 className="font-serif text-xl mb-3">Taslaklar</h2>
        {q.isLoading ? <p className="text-sm text-ink/50">Yükleniyor…</p> : (q.data?.drafts ?? []).length === 0 ? (
          <p className="text-sm text-ink/60">Henüz taslak yok.</p>
        ) : (
          <ul className="divide-y divide-border border-y border-border">
            {q.data!.drafts.map((d) => (
              <li key={d.id}>
                <Link to="/admin/bulten/$id" params={{ id: d.id }} className="grid grid-cols-[minmax(0,1fr)_auto] gap-3 py-3 hover:bg-ink/[0.03]">
                  <span className="min-w-0">
                    <span className="block truncate font-serif text-lg">{d.subject || d.title || "Adsız bülten"}</span>
                    <span className="text-xs text-ink/55">Sayı {d.issue_number ?? "—"} · güncellendi {fmt(d.updated_at)}{d.last_test_at ? ` · test ${fmt(d.last_test_at)}` : ""}</span>
                  </span>
                  <span className="shrink-0 text-xs text-ink/60 self-center">{STATUS[d.status] ?? d.status}</span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section>
        <h2 className="font-serif text-xl mb-1">Gönderim geçmişi</h2>
        <p className="text-xs text-ink/55 mb-3">“Kabul edildi”, e-posta servisinin isteği aldığını gösterir; teslim kanıtı değildir. Geri dönme (bounce) yalnız servis bildirdiğinde sayılır.</p>
        {h.isLoading ? <p className="text-sm text-ink/50">Yükleniyor…</p> : (h.data?.campaigns ?? []).length === 0 ? <p className="text-sm text-ink/60">Kayıt yok.</p> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs text-ink/55"><tr><th className="py-2 pr-3">Tarih</th><th className="pr-3">Konu</th><th className="pr-3 text-right">Kabul</th><th className="pr-3 text-right">Dışı</th><th className="pr-3 text-right">Hata</th><th className="pr-3 text-right">Bounce</th><th className="text-right">Tıklayan</th></tr></thead>
              <tbody className="divide-y divide-border">
                {h.data!.campaigns.map((c) => (
                  <tr key={c.key} title={c.errors.join("\n")}>
                    <td className="py-2 pr-3 whitespace-nowrap">{fmt(c.at)}</td>
                    <td className="pr-3 max-w-[16rem] truncate">{c.template === "newsletter-test" ? "Test · " : c.template === "book_announcement" ? "Kitap · " : ""}{c.subject || "—"}</td>
                    <td className="pr-3 text-right">{c.sent}</td><td className="pr-3 text-right">{c.suppressed}</td><td className="pr-3 text-right">{c.failed}</td><td className="pr-3 text-right">{c.bounced}</td><td className="text-right">{c.clickedRecipients}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <Link to="/admin/newsletter-performance" className="inline-block mt-3 text-sm underline text-ink/70">Ayrıntılı raporlar →</Link>
      </section>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number | undefined }) {
  return (
    <div className="border border-border rounded-lg py-3">
      <div className="font-serif text-2xl">{value ?? "—"}</div>
      <div className="text-[11px] text-ink/55 mt-1">{label}</div>
    </div>
  );
}

function ResendSync() {
  const statusFn = useServerFn(getResendStatus);
  const sync = useServerFn(syncResendChunk);
  const st = useQuery({ queryKey: ["resend-status"], queryFn: () => statusFn() });
  const [prog, setProg] = useState<string | null>(null);
  async function run() {
    setProg("Başlıyor…");
    try {
      let offset = 0;
      for (let i = 0; i < 40; i++) {
        const r = await sync({ data: { offset } });
        offset += r.processed;
        if (r.importedUnsubscribes) toast(`${r.importedUnsubscribes} Resend abonelikten çıkışı veritabanına işlendi`);
        r.errors.forEach((e) => toast.error(e));
        setProg(`${offset} işlendi, ${r.remaining} kaldı`);
        if (r.remaining === 0 || r.processed === 0) break;
      }
      toast.success("Eşitleme tamamlandı");
    } catch (e) { toast.error(e instanceof Error ? e.message : "Eşitleme başarısız"); }
    finally { setProg(null); st.refetch(); }
  }
  const s = st.data;
  return (
    <section className="border border-border rounded-lg p-4 space-y-2">
      <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3">
        <h2 className="min-w-0 font-serif text-xl">Resend gönderim listesi</h2>
        <button onClick={run} disabled={!!prog} className="shrink-0 px-3 py-1.5 border border-ink rounded-full text-xs disabled:opacity-40">{prog ?? "Listeyi eşitle"}</button>
      </div>
      {st.isLoading ? <p className="text-xs text-ink/50">Kontrol ediliyor…</p> : st.error ? <p className="text-xs text-destructive">Resend'e ulaşılamadı.</p> : s && (
        <ul className="text-sm space-y-0.5">
          <li>Resend'deki etkin abone: <strong>{s.inSegment ?? "henüz liste yok"}</strong> / veritabanı {s.eligible}{s.mismatch ? ` · ${s.mismatch} fark` : ""}</li>
          <li>Gönderim dışı (aktarılmaz): {s.suppressed}</li>
          <li className={s.domain.verified ? "" : "text-destructive"}>Gönderici alan adı: {s.domain.verified ? "doğrulandı" : `doğrulanmadı (${s.domain.status})`}</li>
          {s.lastSyncAt && <li className="text-xs text-ink/55">Son eşitleme: {fmt(s.lastSyncAt)}</li>}
        </ul>
      )}
    </section>
  );
}
